'use client';

import { useIsDesktop } from '@/_hooks/useBreakpoint';
import Header from '@/app/[locale]/(home)/_components/Header';
import Breadcrumbs from '@/_components/Breadcrumbs';
import BreadcrumbJsonLd from '@/_components/Breadcrumbs/BreadcrumbJsonLd';
import { buildCatalogBreadcrumbs } from '@/_components/Breadcrumbs/buildCatalogBreadcrumbs';
import { getProductHref } from '@/_lib/product-href';
import { Box } from '@chakra-ui/react';
import ProductResponsive from '../../responsive';
import Favourites from '../Favourites';
import { useQuery } from '@tanstack/react-query';
import productService from '@/_service/product.service';
import getShipperById from '@/_service/shipper.service';
import { shallowEqual, useSelector } from 'react-redux';
import { IRedux } from '@/_store/types';
import { useActiveBrand } from '@/_hooks/useActiveBrand';
import { useMemo } from 'react';
import type { IProduct } from '@/entities/product/model/types';
import { useTranslations } from 'next-intl';
import { useCustomLocale } from '@/_hooks/useCustomLocale';
import { BRAND_NAMES } from '@/_utils/Constants';

const Page = ({
  slug,
  initialProduct,
  initialShipperId,
}: {
  slug: string;
  initialProduct?: IProduct | null;
  initialShipperId?: string;
}) => {
  const isDesktop = useIsDesktop();
  const t = useTranslations();
  const currentLocale = useCustomLocale();

  const { branches } = useSelector(
    (state: IRedux) => ({
      branches: state.common.branches,
    }),
    shallowEqual,
  );

  const shipperId = useActiveBrand() || initialShipperId || '';
  const branch = branches?.[shipperId];

  const selectedMenuId = branch?.menu_id || '';

  const categoryIds = useMemo(
    () => String(initialProduct?.categories?.map((item) => item.id) || ''),
    [initialProduct?.categories],
  );

  const mappedBrandName = shipperId ? BRAND_NAMES[shipperId] : undefined;

  const { data: shipper } = useQuery({
    queryKey: ['shipper-for-breadcrumbs', shipperId],
    queryFn: () => getShipperById(shipperId).then((res) => res.data),
    enabled: Boolean(shipperId) && !mappedBrandName,
  });

  const brandName = mappedBrandName || shipper?.name || '';

  const breadcrumbData = useMemo(() => {
    if (!brandName || !shipperId) return null;

    const productTitle =
      initialProduct?.title?.[currentLocale] || initialProduct?.title?.ru || '';
    if (!productTitle) return null;

    const primaryCategory = initialProduct?.categories?.[0];
    const categoryTitle = primaryCategory?.title?.[currentLocale];

    return buildCatalogBreadcrumbs({
      homeLabel: t('home'),
      brand: { name: brandName, id: shipperId },
      category:
        primaryCategory?.slug && categoryTitle
          ? { title: categoryTitle, slug: primaryCategory.slug }
          : null,
      productTitle,
    });
  }, [brandName, shipperId, initialProduct, currentLocale, t]);

  const { data: favourites } = useQuery({
    queryKey: ['product-favourites', categoryIds, selectedMenuId, shipperId],
    queryFn: () =>
      productService
        .getProductFavourites(
          {
            category_ids: categoryIds,
            menu_id: selectedMenuId,
          },
          shipperId,
        )
        .then((res) => res.data),
    enabled: Boolean(categoryIds && shipperId),
  });

  return (
    <Box bg='light.300' minH='dvh'>
      {isDesktop && <Header />}
      {isDesktop && breadcrumbData && (
        <Box maxW='1200px' mx='auto' px={{ base: 4, lg: 0 }}>
          <Breadcrumbs data={breadcrumbData} />
        </Box>
      )}
      {breadcrumbData && (
        <BreadcrumbJsonLd
          data={breadcrumbData}
          currentHref={getProductHref(
            initialProduct?.slug || slug,
            shipperId,
          )}
        />
      )}
      <Box
        mx='auto'
        bg={'light'}
        rounded={16}
        maxW='1200px'
        mt={{ base: 0, lg: breadcrumbData ? 0 : 10 }}
        mb={{ base: 0, lg: 10 }}
      >
        <ProductResponsive
          shipperId={shipperId}
          slug={slug}
          initialProduct={initialProduct}
        />
      </Box>
      {isDesktop && (
        <Box maxW='1200px' mx='auto' mb={12}>
          <Favourites data={favourites} />
        </Box>
      )}
    </Box>
  );
};

export default Page;
